import { useCallback, useState } from 'react';
import { uploadChecklistPhoto } from '@apis/checklists';
import type { ITaskAttachment } from '@interfaces/IScheduler';
import { photoRefusal, uploadRefusal, type PhotoRefusal } from '../lib/photoRefusal';

/** Where an evidence photo is stored: one check item of a task on its loaded schedule. */
export interface IEvidenceTarget {
  siteId: string;
  scheduleNumber: string;
  taskId: string;
  itemId: string;
}

export interface IChecklistPhotoUpload {
  /** The stored photo, once uploaded. */
  photo: ITaskAttachment | undefined;
  uploading: boolean;
  /** Why the last photo was not stored, or null. */
  failure: PhotoRefusal | null;
  /** Store `file` as the item's evidence — undefined with nowhere to store
   *  it (no loaded schedule), and then no photo can be taken. */
  take: ((file: File) => Promise<void>) | undefined;
  /** Let the stored photo go (it is only referenced once a tick carries it). */
  clear: () => void;
}

/**
 * The capture form's photo: uploaded FIRST, to the task's evidence store, so
 * the tick can reference what was stored (task-checklist design, decision 8).
 */
export function useChecklistPhotoUpload(target: IEvidenceTarget | undefined): IChecklistPhotoUpload {
  const [photo, setPhoto] = useState<ITaskAttachment | undefined>();
  const [uploading, setUploading] = useState(false);
  const [failure, setFailure] = useState<PhotoRefusal | null>(null);

  const upload = useCallback(async (to: IEvidenceTarget, file: File) => {
    // A file the server would refuse is said so now, not after an upload.
    const refusal = photoRefusal(file);
    setFailure(refusal);
    if (refusal) return;
    setUploading(true);
    try {
      setPhoto(await uploadChecklistPhoto(to.siteId, to.scheduleNumber, to.taskId, to.itemId, file, file.name));
    } catch (error) {
      setFailure(uploadRefusal(error));
    } finally {
      setUploading(false);
    }
  }, []);
  const clear = useCallback(() => setPhoto(undefined), []);

  return { photo, uploading, failure, take: target && ((file: File) => upload(target, file)), clear };
}
